import { api } from '@hunk/shared/api';
import type { StartPaymentRes } from '@hunk/shared/api/orders';
import { type PaymentOptionType, PaymentOptionTranslated } from '@hunk/shared/common';
import { useEffect, useState } from 'react';
import type { NavigateFunction } from 'react-router';

import { IS_MINI_APP } from '@/appMode';
import { PaymentOptionIcon } from '@/components/PaymentOptionIcon';
import { walletAllowed } from '@/lib/walletDevice';
import { cn } from '@/lib/utils';

/** the options an admin enabled and the server can take, minus the wallet this device can't use */
export function usePaymentOptions() {
  const [options, setOptions] = useState<PaymentOptionType[]>();
  useEffect(() => {
    api.billing
      .getOptions()
      .then((r) => setOptions((r.response.paymentOptions ?? []).filter((o) => (o === 'apay' ? walletAllowed('apple') : o === 'gpay' ? walletAllowed('google') : true))))
      .catch(() => setOptions([]));
  }, []);
  return options;
}

export function PaymentPicker({ options, value, onChange }: { options: PaymentOptionType[]; value?: PaymentOptionType; onChange: (o: PaymentOptionType) => void }) {
  if (!options.length) return <p className="rounded-lg bg-amber-500/10 p-3 text-sm text-amber-400">אין כרגע אמצעי תשלום זמין. נסו שוב מאוחר יותר.</p>;
  return (
    <div className="flex flex-col divide-y divide-white/10 overflow-hidden rounded-xl border border-white/10">
      {options.map((o) => (
        <button
          key={o}
          type="button"
          onClick={() => onChange(o)}
          className={cn('flex items-center gap-3 px-3 py-3 text-start transition-colors', o === value ? 'bg-primary/15' : 'hover:bg-white/5')}
        >
          <span className={cn('flex size-4 shrink-0 items-center justify-center rounded-full border-2', o === value ? 'border-primary' : 'border-neutral-500')}>
            {o === value && <span className="bg-primary size-2 rounded-full" />}
          </span>
          <span className="flex-1 font-semibold">{PaymentOptionTranslated[o]}</span>
          <PaymentOptionIcon option={o} />
        </button>
      ))}
    </div>
  );
}

/** Mini App: these open in the phone's browser instead of our in-app frame (wallet APIs / PayPal can't be framed) */
const EXTERNAL_IN_MINI: PaymentOptionType[] = ['paypal', 'gpay', 'apay'];

/**
 * Opens the provider's page for a started payment (same rules as bb365):
 *   website -> full page (comes back through /api/billing/return to /order/<id>?tx=)
 *   Mini App -> card / Bit in an overlay frame on the order page; PayPal / wallets in the phone's browser
 */
export function openPayment(r: StartPaymentRes, option: PaymentOptionType, navigate: NavigateFunction) {
  if (!r.url || r.orderId == null) return;
  const orderPath = `/order/${r.orderId}?tx=${encodeURIComponent(r.internalTransactionId ?? '')}`;
  if (!IS_MINI_APP) {
    window.location.href = r.url;
    return;
  }
  if (EXTERNAL_IN_MINI.includes(option)) {
    const w = window.Telegram?.WebApp;
    if (w?.openLink) w.openLink(r.url, { try_instant_view: false });
    else window.open(r.url, '_blank', 'noopener');
    navigate(orderPath, { state: { external: true } });
  } else navigate(orderPath, { state: { frameUrl: r.url } });
}
